import { useEffect, useState } from 'react';
import { X, MessageSquare, AlertTriangle, CheckCircle, Info, AlertCircle } from 'lucide-react';
import { useToastStore, Toast } from '@/stores/toastStore';

function ToastIcon({ type }: { type: Toast['type'] }) {
  switch (type) {
    case 'mention':
      return <MessageSquare className="h-5 w-5 text-brand-500" />;
    case 'success':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-amber-500" />;
    case 'error':
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    default:
      return <Info className="h-5 w-5 text-blue-500" />;
  }
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleDismiss = () => {
    setExiting(true);
    setTimeout(onDismiss, 200);
  };

  const handleAction = () => {
    toast.action?.onClick();
    handleDismiss();
  };

  return (
    <div
      className={`
        pointer-events-auto w-80 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10
        transition-all duration-200 ease-out
        ${visible && !exiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex-shrink-0 mt-0.5">
          {toast.icon ? (
            <span className="text-xl">{toast.icon}</span>
          ) : (
            <ToastIcon type={toast.type} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {toast.title}
          </p>
          {toast.message && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
              {toast.message}
            </p>
          )}
          {toast.action && (
            <button
              onClick={handleAction}
              className="mt-2 text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
}

export default function ToastContainer() {
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col-reverse gap-2 pointer-events-none">
      {toasts.slice(-5).map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}
